//src/components/home/HomeToolsSection.js
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styles from '../../styles/home/HomeProductSection.module.css';
import { fetchHomeAIUrl, fetchGPTCarIds } from '../../api/orderApi';
import LoginModal from '../LoginModal';
import AlertModal from '../AlertModal';

// 格式化日期函数
const formatExpiryDate = (dateString) => {
  if (!dateString) return '未设置'; // 这个也可以国际化，但先保持现状
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}.${month}.${day}`;
};

// 到期时间字段映射
const expiryDateMap = {
  gpt: 'expiration_date',
  claude: 'expiration_date_ye_claude',
  mj: 'expiration_date_ye_mj',
  grok: 'expiration_date_ye_grok',
  googlescholar: 'expiration_date',  // 假设使用相同字段
  ppt: 'expiration_date'              // 假设使用相同字段
};

const HomeToolsSection = ({ id }) => {
  // 使用 i18n 的翻译钩子
  const { t } = useTranslation();

  // 工具数据使用翻译键
  const homeTools = [
    { 
      id: 'googlescholar', 
      name: t('homeTools.googleScholar.name'), 
      image: `${process.env.PUBLIC_URL}/images/google-scholar-main.jpg`, 
      link: '/fleet' 
    },
    { 
      id: 'ppt', 
      name: t('homeTools.ppt.name'), 
      image: `${process.env.PUBLIC_URL}/images/ppt-main.jpg`, 
      link: '/fleet' 
    },
  ];

  const user = useSelector((state) => state.user.user);
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [isLoading, setIsLoading] = useState({});
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(true);

  console.log('HomeToolsSection rendered:', { user, isLoggedIn, localStorage });

  const handleUseNowClick = async (productId) => {
    // 如果在维护模式下，直接返回，不执行点击逻辑
    if (isMaintenanceMode) return;

    // 检查是否已在加载中
    if (isLoading[productId]) return;

    const usertoken = user?.usertoken || localStorage.getItem('usertoken');
    const host = window.location.hostname;

    if (!usertoken || !isLoggedIn) {
      console.log('用户未登录或缺少 usertoken，显示登录弹窗');
      setIsLoginModalOpen(true);
      return;
    }

    if (!host) {
      console.error('无法获取 host，当前域名为空');
      setAlertMessage(t('homeTools.messages.noHost'));
      setIsAlertModalOpen(true);
      return;
    }

    // 立即显示弹窗并设置加载状态
    setAlertMessage(t('homeTools.messages.loading'));
    setIsAlertModalOpen(true);
    setIsLoading((prev) => ({ ...prev, [productId]: true }));

    try {
      if (productId === 'gpt') {
        const requestData = { host, usertoken };
        const response = await fetchGPTCarIds(requestData);

        if (response.message === 'Success' && response.url) {
          window.open(response.url, '_blank');
          setAlertMessage(t('homeTools.messages.redirectSuccess'));
        } else {
          setAlertMessage(t('homeTools.messages.requestFailed', { error: response.data?.message || t('homeTools.messages.unknownError') }));
          console.log(`fetchGPTCarIds 请求成功但 message 不是 Success: ${response.data?.message}`);
        }
      } else {
        const user_key = user?.user_key || localStorage.getItem('user_key');
        if (!user_key) {
          console.log('缺少 user_key，显示登录弹窗');
          setIsLoginModalOpen(true);
          setIsAlertModalOpen(false);
          return;
        }

        const requestData = { user_key, type: productId };
        const response = await fetchHomeAIUrl(requestData);

        if (response.message === 'Success' && response.data.url) {
          window.open(response.data.url, '_blank');
          setAlertMessage(t('homeTools.messages.redirectSuccess'));
        } else {
          setAlertMessage(t('homeTools.messages.requestFailed', { error: response.data?.message || t('homeTools.messages.unknownError') }));
          console.log(`fetchHomeAIUrl 请求成功但 message 不是 Success: ${response.data?.message}`);
        }
      }
    } catch (error) {
      console.error(`获取 ${productId} 数据失败:`, error.message);
      setAlertMessage(t('homeTools.messages.requestFailed', { error: error.message }));
    } finally {
      // 清除加载状态，但保留弹窗让用户手动关闭
      setIsLoading((prev) => ({ ...prev, [productId]: false }));
    }
  };

  const handleCloseModal = () => {
    setIsLoginModalOpen(false);
  };

  const handleLoginSuccess = () => {
    setIsLoginModalOpen(false);
  };

  const handleAlertClose = () => {
    setIsAlertModalOpen(false);
  };

  // 获取产品的到期时间
  const getExpiryDate = (productId) => {
    if (!isLoggedIn || !user) return null;
    const field = expiryDateMap[productId];
    return user[field] || null;
  };

  // 测试用：手动切换维护模式的函数（可选）
  const toggleMaintenanceMode = () => {
    setIsMaintenanceMode((prev) => !prev);
  };

  return (
    <section id={id} className={styles.homeProductSection}>
      <div className={styles.container}>
        <h2 className={styles.sectionTitle}>{t('homeTools.sectionTitle')}</h2>
        <div className={styles.productGrid}>
          {homeTools.map((product) => {
            const expiryDate = getExpiryDate(product.id);
            return (
              <div key={product.id} className={styles.productCard}>
                <div className={styles.imageContainer}>
                  <img
                    src={product.image}
                    alt={t('homeTools.imageAlt', { name: product.name })}
                    className={styles.mainImage}
                  />
                </div>
                <h3>{product.name}</h3>

                {/* 仅在用户登录且有到期时间时显示 */}


                <button
                  onClick={() => handleUseNowClick(product.id)}
                  className={styles.btn}
                  disabled={isLoading[product.id] || isMaintenanceMode}
                >
                  {isLoading[product.id] ? t('homeTools.buttons.redirecting') : t('homeTools.buttons.unavailable')}
                </button>
              </div>
            );
          })}
        </div>

        {/* 测试用：切换维护模式的按钮（可选） */}
        {/* <button onClick={toggleMaintenanceMode} className={styles.toggleBtn}>
          {isMaintenanceMode ? t('homeTools.buttons.enableClick') : t('homeTools.buttons.disableClick')}
        </button> */}
      </div>

      {/* 登录弹窗 */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={handleCloseModal}
        onLoginSuccess={handleLoginSuccess}
      />

      {/* 提示弹窗 */}
      <AlertModal
        isOpen={isAlertModalOpen}
        message={alertMessage}
        onClose={handleAlertClose}
      />
    </section>
  );
};

export default React.memo(HomeToolsSection);